import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Building2, FileText, Users, Wrench,
  CreditCard, Menu, LogOut, ChevronLeft, Home, FileSignature,
  FolderOpen, BarChart3, ShieldCheck, DollarSign, ScrollText,
  MessageSquare, Hammer, AlertTriangle,
} from "lucide-react";
import ChatBubble from "@/components/ChatBubble";

const tenantNav = [
  { label: "Dashboard", href: "/tenant", icon: LayoutDashboard },
  { label: "My Application", href: "/tenant/apply", icon: FileText },
  { label: "Leases", href: "/tenant/leases", icon: FileSignature },
  { label: "Documents", href: "/tenant/documents", icon: FolderOpen },
  { label: "Payments", href: "/tenant/payments", icon: CreditCard },
  { label: "Maintenance", href: "/tenant/maintenance", icon: Wrench },
];

const adminNav = [
  { label: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { label: "Rooms", href: "/admin/rooms", icon: Building2 },
  { label: "Applications", href: "/admin/applications", icon: FileText },
  { label: "Leases", href: "/admin/leases", icon: FileSignature },
  { label: "Documents", href: "/admin/documents", icon: FolderOpen },
  { label: "Users", href: "/admin/users", icon: Users },
  { label: "Maintenance", href: "/admin/maintenance", icon: Wrench },
  { label: "Reports", href: "/admin/reports", icon: BarChart3 },
];

const sysAdminNav = [
  { label: "System Admin", href: "/system-admin", icon: ShieldCheck },
  { label: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { label: "Rooms", href: "/admin/rooms", icon: Building2 },
  { label: "Applications", href: "/admin/applications", icon: FileText },
  { label: "Leases", href: "/admin/leases", icon: FileSignature },
  { label: "Documents", href: "/admin/documents", icon: FolderOpen },
  { label: "Users", href: "/admin/users", icon: Users },
  { label: "Maintenance", href: "/admin/maintenance", icon: Wrench },
  { label: "Finance", href: "/finance", icon: DollarSign },
  { label: "Property", href: "/property", icon: Hammer },
  { label: "Reports", href: "/admin/reports", icon: BarChart3 },
];

const financeNav = [
  { label: "Finance", href: "/finance", icon: DollarSign },
];

const propertyNav = [
  { label: "Property Mgmt", href: "/property", icon: Hammer },
];

export default function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isTenantPortal = user?.role === "tenant";
  const navItems =
    user?.role === "super_admin" ? sysAdminNav :
    user?.role === "admin" ? adminNav :
    user?.role === "finance" ? financeNav :
    user?.role === "operations" ? propertyNav :
    tenantNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  const SidebarContent = ({ onNavigate }) => (
    <div className="flex flex-col h-full bg-slate-900">
      <div className="p-4 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2" onClick={onNavigate}>
          <div className="h-8 w-8 rounded-lg bg-sky-500 flex items-center justify-center shrink-0">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div>
              <span className="font-heading text-sm font-extrabold tracking-tight leading-none text-white">LEGACY</span>
              <span className="font-heading text-[8px] font-bold tracking-[0.3em] block leading-none text-amber-400">HEIGHTS</span>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const active = location.pathname === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavigate}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                ${active
                  ? "bg-sky-500 text-white shadow-lg shadow-sky-500/25"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/10">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 hover:bg-white/5 hover:text-slate-300 transition-colors"
        >
          <Home className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Back to Website</span>}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      <aside className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen z-40 transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}>
        <SidebarContent onNavigate={() => {}} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 bg-slate-900 border border-slate-700 rounded-full p-1 shadow-lg hover:bg-slate-800 transition-colors"
          data-testid="sidebar-toggle"
        >
          <ChevronLeft className={`h-3 w-3 text-white transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>

      <div className={`transition-all duration-200 ${collapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-sidebar-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-60 p-0 border-none">
                  <SidebarContent onNavigate={() => setMobileOpen(false)} />
                </SheetContent>
              </Sheet>
              <h1 className="font-heading text-lg font-bold text-slate-900" data-testid="page-title">{title}</h1>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-sky-500 text-white text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-900 leading-none">{user?.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
        {isTenantPortal && (
          <ChatBubble botType="tenant" title="Tenant Support" subtitle="How can we help?" accentColor="amber" />
        )}
      </div>
    </div>
  );
}
